'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  type TextFieldProps,
} from '@/primitives';
import { SearchIcon } from '@/components/icons';
import { FilterOptionsState } from '@mui/material';

export interface BaseSearchFieldApi {
  reset: () => void;
}

export interface BaseSearchFieldProps<TValue = string> {
  autoCompleteId?: string;
  disabled?: boolean;
  freeSolo?: boolean;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  InputProps?: TextFieldProps['InputProps'];
  fetchOptions: (inputValue: string) => Promise<readonly TValue[]>;
  fullWidth?: boolean;
  getOptionLabel: (option: string | TValue) => string;
  isOptionEqualToValue: (option: TValue, value: TValue) => boolean;
  label: ReactNode;
  noOptionsText?: ReactNode;
  onChange?: (value: TValue | null) => void;
  placeholder: string;
  renderOption: (props: Object, option: TValue) => ReactNode;
  value?: TValue | null;
}

const defaultNoOptionsText = 'No matches found';

function BaseSearchFieldInner<TValue>(
  {
    autoCompleteId,
    disabled,
    freeSolo,
    InputLabelProps,
    InputProps,
    fetchOptions,
    fullWidth,
    getOptionLabel,
    isOptionEqualToValue,
    label,
    noOptionsText,
    onChange,
    placeholder,
    renderOption,
    value,
  }: BaseSearchFieldProps<TValue>,
  ref: Ref<BaseSearchFieldApi>,
) {
  const [loading, setLoading] = useState(false);
  const [internalValue, setInternalValue] = useState<TValue | null>(
    value !== undefined ? value : null,
  );
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly TValue[]>([]);

  const fetchedOnce = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInternalValue(value !== undefined ? value : null);
  }, [value]);

  useImperativeHandle(
    ref,
    () => {
      return {
        reset: () => {
          setInternalValue(null);
          setInputValue('');
          setOptions([]);
        },
      };
    },
    [],
  );

  useEffect(() => {
    if (inputValue) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (internalValue && getOptionLabel(internalValue) === inputValue) {
        return;
      }

      setLoading(true);

      timerRef.current = setTimeout(() => {
        (async () => {
          const results = await fetchOptions(inputValue);
          const sorted = results.toSorted((a, b) =>
            getOptionLabel(a).localeCompare(getOptionLabel(b)),
          );
          setOptions(sorted);
          setLoading(false);
          fetchedOnce.current = true;
        })();
      }, 600);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        setLoading(false);
      }
    };
  }, [internalValue, inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // To turn of suggestion pop-up for Aucomplete
  useEffect(() => {
    if (autoCompleteId) {
      document
        .getElementById(autoCompleteId)
        ?.setAttribute('autocomplete', 'no-address-popup');
    }
    //eslint-disable-next-line
  }, []);

  // show defaultNoOptionsText when freeSolo = true
  function handleFilterOptions(
    options: TValue[],
    state: FilterOptionsState<TValue>,
  ) {
    if (
      freeSolo &&
      options.length === 0 &&
      fetchedOnce.current &&
      !loading &&
      state.inputValue
    ) {
      return [defaultNoOptionsText] as TValue[];
    }
    return options;
  }

  // show defaultNoOptionsText when freeSolo = true
  function handleRenderOption(props: Object, option: TValue) {
    return option === defaultNoOptionsText ? (
      <li
        {...props}
        key={defaultNoOptionsText}
        style={{ pointerEvents: 'none', color: 'gray' }}
      >
        {option as string}
      </li>
    ) : (
      renderOption(props, option)
    );
  }

  return (
    <Autocomplete
      id={autoCompleteId}
      disabled={disabled}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel}
      filterOptions={handleFilterOptions}
      filterSelectedOptions
      fullWidth={fullWidth}
      includeInputInList
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={false}
      noOptionsText={noOptionsText || defaultNoOptionsText}
      onChange={(event, newValue) => {
        if (!newValue) {
          setOptions([]);
          setInternalValue(null);
          onChange?.(null);
        } else if (typeof newValue === 'object') {
          setInternalValue(newValue);
          onChange?.(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      renderInput={(props) => (
        <TextField
          {...props}
          variant="filled"
          label={label}
          placeholder={placeholder}
          InputLabelProps={{ ...InputLabelProps, ...props?.InputLabelProps }}
          InputProps={{
            ...props?.InputProps,
            ...InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    sx={{ marginTop: '-16px' }}
                  />
                ) : (
                  <SearchIcon
                    className="search-icon"
                    sx={{
                      position: 'absolute',
                      bottom: '14px',
                      right: '8px',
                      opacity: 0.54,
                    }}
                  />
                )}
                {props.InputProps.endAdornment}
              </>
            ),
            sx: {
              '&:has(input:not(:placeholder-shown)) .search-icon': {
                display: 'none',
              },
              '& input:placeholder-shown + MuiAutocomplete-endAdornment': {
                display: 'none',
              },
            },
          }}
        />
      )}
      renderOption={handleRenderOption}
      value={internalValue}
    />
  );
}

// This is a workaround for forwardRef not passing through generic
// prop parameters
declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: Ref<T>) => ReactNode | null,
  ): (props: P & RefAttributes<T>) => ReactNode | null;
}

export const BaseSearchField = forwardRef(BaseSearchFieldInner);
